/* ============================================================
   DoseTrack Organiser — WebGL product stage

   Geometry traced from the product photography (IMG_5690 side
   profile, IMG_5679/5688 open latch, IMG_5697/5708 exploded):

     · fat pebble shell, D/H ≈ 2.9, widest at mid-height —
       lathed with a REAL GAP over the latch span, so opening
       the latch reveals the wedge behind it
     · white 7-vane carousel; the wedges have no outer wall of
       their own — the shell's well wall closes them, except at
       the latch opening
     · dished tinted lid with a centre hole and raised collar
     · the LATCH: a tinted flap seated in the rim gap, hinged
       at its BOTTOM edge — it swings down and out, and the
       medication spills over the sill
     · button = the carousel hub, poking up through the lid

   The hero is PURPLE — the colourway of the reference
   photography — and never changes colour; colour selection
   lives with the photographs in the buy module.

   Every motion is a pure function of scroll progress.
   ============================================================ */

function createStage(canvas, opts = {}) {
  const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* the aspect the framing was composed at, and the resulting camera pull-back
     factor — see resize() */
  const REF_ASPECT = 1.55;
  let fit = 1;

  const renderer = new THREE.WebGLRenderer({
    canvas, antialias: true, alpha: true, powerPreference: "high-performance",
    preserveDrawingBuffer: true
  });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.10;
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene  = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(27, 1, 0.1, 100);

  /* ---------- procedural studio env (instant), real HDRI (async) ---------- */
  function studioEnv() {
    const c = document.createElement("canvas");
    c.width = 1024; c.height = 512;
    const x = c.getContext("2d");
    const g = x.createLinearGradient(0, 0, 0, 512);
    g.addColorStop(0.00, "#ffffff"); g.addColorStop(0.32, "#eaeef6");
    g.addColorStop(0.52, "#bcc4d4"); g.addColorStop(0.76, "#6d7587");
    g.addColorStop(1.00, "#2c3141");
    x.fillStyle = g; x.fillRect(0, 0, 1024, 512);
    const box = (cx, cy, w, h, a) => {
      const r = x.createRadialGradient(cx, cy, 0, cx, cy, Math.max(w, h));
      r.addColorStop(0, `rgba(255,255,255,${a})`);
      r.addColorStop(1, "rgba(255,255,255,0)");
      x.fillStyle = r;
      x.save(); x.translate(cx, cy); x.scale(1, h / w); x.translate(-cx, -cy);
      x.beginPath(); x.arc(cx, cy, w, 0, Math.PI * 2); x.fill(); x.restore();
    };
    box(250, 118, 215, 132, 1.0); box(762, 162, 168, 112, 0.60);
    box(505, 38, 430, 92, 0.55);
    const tex = new THREE.CanvasTexture(c);
    tex.mapping = THREE.EquirectangularReflectionMapping;
    tex.colorSpace = THREE.SRGBColorSpace;
    const pm = new THREE.PMREMGenerator(renderer);
    const env = pm.fromEquirectangular(tex).texture;
    pm.dispose(); tex.dispose();
    return env;
  }
  scene.environment = studioEnv();

  const key = new THREE.DirectionalLight(0xffffff, 1.45);
  key.position.set(-2.4, 3.4, 2.6); scene.add(key);
  const rimL = new THREE.DirectionalLight(0xdce6ff, 1.05);
  rimL.position.set(2.8, 1.0, -2.4); scene.add(rimL);
  const amb = new THREE.AmbientLight(0xffffff, 0.26); scene.add(amb);

  if (opts.hdr) {
    try {
      const bin = Uint8Array.from(atob(opts.hdr), ch => ch.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bin]));
      new RGBELoader().load(url, tex => {
        tex.mapping = THREE.EquirectangularReflectionMapping;
        const pm = new THREE.PMREMGenerator(renderer);
        scene.environment = pm.fromEquirectangular(tex).texture;
        pm.dispose(); tex.dispose(); URL.revokeObjectURL(url);
        key.intensity = 0.55; rimL.intensity = 0.42; amb.intensity = 0.10;
        renderer.toneMappingExposure = 1.22;
      }, undefined, e => console.warn("HDR env failed, procedural stays", e));
    } catch (e) { console.warn("HDR decode failed", e); }
  }

  /* ---------- materials ---------- */
  function mottle(base, amp) {
    const N = 256, c = document.createElement("canvas");
    c.width = c.height = N;
    const x = c.getContext("2d"), d = x.createImageData(N, N);
    for (let i = 0; i < N * N; i++) {
      const v = base + (Math.random() - 0.5) * amp;
      d.data[i*4] = d.data[i*4+1] = d.data[i*4+2] = v * 255;
      d.data[i*4+3] = 255;
    }
    x.putImageData(d, 0, 0);
    const t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(3, 3);
    return t;
  }
  const body = new THREE.MeshPhysicalMaterial({
    color: 0xf2f4f8, roughness: 1, roughnessMap: mottle(0.27, 0.10),
    metalness: 0, clearcoat: 1, clearcoatRoughness: 0.09,
    envMapIntensity: 1.05, side: THREE.DoubleSide
  });
  const bodyDeep = new THREE.MeshPhysicalMaterial({
    color: 0xe6eaf1, roughness: 0.46, metalness: 0, envMapIntensity: 0.85
  });
  /* tinted polycarbonate, PURPLE — matched to the reference photography */
  const tint = new THREE.MeshPhysicalMaterial({
    color: 0xffffff, roughness: 0.02, metalness: 0,
    transmission: 0.98, thickness: 0.14, ior: 1.52,
    attenuationColor: new THREE.Color(0x6C34C9), attenuationDistance: 0.42,
    clearcoat: 1, clearcoatRoughness: 0.02,
    iridescence: 0.55, iridescenceIOR: 1.35,
    transparent: true, envMapIntensity: 1.15, side: THREE.DoubleSide
  });

  const product = new THREE.Group();
  const V = (r, y) => new THREE.Vector2(r, y);
  const SEG = (Math.PI * 2) / 7;

  /* the latch span — shared by the shell gap, the latch and the spill */
  const SPAN = SEG * 0.94;
  const A0   = Math.PI * 0.86;
  const AMID = A0 + SPAN / 2;
  const DD = new THREE.Vector3(Math.cos(AMID), 0, Math.sin(AMID));   // outward
  const TT = new THREE.Vector3(-Math.sin(AMID), 0, Math.cos(AMID));  // tangent

  /* ---------- shell, with a real opening where the latch sits ---------- */
  const shellProfile = [
    V(0.001, -0.400), V(0.680, -0.398), V(0.780, -0.380), V(0.860, -0.345),
    V(0.925, -0.288), V(0.970, -0.210), V(0.993, -0.110), V(1.000, -0.010),
    V(0.994,  0.085), V(0.975,  0.163), V(0.944,  0.225), V(0.902,  0.268),
    V(0.868,  0.288),
    V(0.852,  0.272), V(0.845,  0.180), V(0.842, -0.060),
    V(0.820, -0.115), V(0.740, -0.150), V(0.001, -0.155)
  ];
  /* lathe phi runs (sinφ, cosφ); our angles run (cos a, sin a): φ = π/2 − a.
     The main shell stops one SPAN short, leaving room for the window piece. */
  const shell = new THREE.Mesh(
    new THREE.LatheGeometry(shellProfile, 150, Math.PI / 2 - A0,
                            Math.PI * 2 - SPAN), body);
  shell.geometry.computeVertexNormals();
  product.add(shell);

  /* The latch window: same silhouette as the shell over the latch span, but
     between sill and lintel the surface dives inward to a POCKET back wall.
     The real opening is a small mouth in the rim — not a hole through the
     product — so what you see inside is a shallow lined chamber. */
  const SILL_Y = -0.255, LINTEL_Y = 0.075, BACK_R = 0.858;
  const winProfile = [
    V(0.001, -0.400), V(0.680, -0.398), V(0.780, -0.380), V(0.860, -0.345),
    V(0.925, -0.288), V(0.952, SILL_Y),          // outer curve up to the sill
    V(0.872, SILL_Y + 0.004), V(0.858, SILL_Y + 0.014),   // sill turns inward
    V(BACK_R, LINTEL_Y - 0.013),                 // pocket back wall
    V(0.968, LINTEL_Y),                          // lintel back out to the shell
    V(0.994,  0.085), V(0.975,  0.163), V(0.944,  0.225), V(0.902,  0.268),
    V(0.868,  0.288),
    V(0.852,  0.272), V(0.845,  0.180), V(0.842, -0.060),
    V(0.820, -0.115), V(0.740, -0.150), V(0.001, -0.155)
  ];
  const bodyPlain = new THREE.MeshPhysicalMaterial({
    color: 0xf2f4f8, roughness: 0.27, metalness: 0,
    clearcoat: 1, clearcoatRoughness: 0.09, envMapIntensity: 1.05,
    side: THREE.DoubleSide });
  const winPiece = new THREE.Mesh(
    new THREE.LatheGeometry(winProfile, 56, Math.PI / 2 - A0 - SPAN, SPAN),
    bodyPlain);
  winPiece.geometry.computeVertexNormals();
  product.add(winPiece);

  /* the chamber you see through the mouth is purple tinted plastic */
  const liner = new THREE.Mesh(
    new THREE.LatheGeometry([
      V(0.868, SILL_Y + 0.020), V(0.866, LINTEL_Y - 0.020)
    ], 40, Math.PI / 2 - A0 - SPAN + 0.03, SPAN - 0.06), tint);
  product.add(liner);

  /* thin cheek walls closing the slit where the two profiles part company */
  const cheekGeo = new THREE.BoxGeometry(0.15, LINTEL_Y - SILL_Y, 0.012);
  [A0, A0 + SPAN].forEach(a => {
    const w = new THREE.Mesh(cheekGeo, bodyPlain);
    w.position.set(Math.cos(a) * 0.912, (SILL_Y + LINTEL_Y) / 2,
                   Math.sin(a) * 0.912);
    w.rotation.y = -a;
    product.add(w);
  });

  /* mould parting line, skipping the window span */
  const seam = new THREE.Mesh(
    new THREE.TorusGeometry(1.0005, 0.0028, 8, 200,
                            Math.PI * 2 - SPAN).rotateX(Math.PI / 2),
    new THREE.MeshStandardMaterial({ color: 0xc4cad6, roughness: 0.6 }));
  seam.position.y = -0.010;
  seam.rotation.y = -(A0 + SPAN);
  shell.add(seam);

  const wellFloor = new THREE.Mesh(
    new THREE.CircleGeometry(0.90, 96).rotateX(-Math.PI / 2), bodyDeep);
  wellFloor.position.y = -0.152;
  product.add(wellFloor);

  /* ---------- carousel: base + radial vanes (no outer wall of its own) --- */
  const carousel = new THREE.Group();
  const cBase = new THREE.Mesh(new THREE.CylinderGeometry(0.826, 0.820, 0.026, 96), body);
  cBase.position.y = -0.136; carousel.add(cBase);

  const HUB_R = 0.150, OUT_R = 0.826, VANE_TOP = 0.232;
  const vaneGeo = new THREE.BoxGeometry(OUT_R - HUB_R, VANE_TOP + 0.115, 0.026);
  for (let i = 0; i < 7; i++) {
    const a = i * SEG;
    const v = new THREE.Mesh(vaneGeo, body);
    v.position.set(Math.cos(a) * (HUB_R + OUT_R) / 2, (VANE_TOP - 0.115) / 2,
                   Math.sin(a) * (HUB_R + OUT_R) / 2);
    v.rotation.y = -a;
    carousel.add(v);
  }
  product.add(carousel);

  /* ---------- medication: two-part gel capsules + tablets, true scale ----
     A real capsule is ~19×7 mm against this 90 mm product: length ≈ 0.42
     of the shell radius. Undersized pills were the loudest "CG" tell. */
  const CAP_SPECS = [
    [0x2FA8A0, 0xF4F6F8],   // teal / white
    [0x3F7F4E, 0x9FD3A8],   // green / pale green (the reference photo pair)
    [0x2F6EB0, 0xEDF1F5],   // blue / white
    [0xC96A1E, 0xF2E4C8],   // amber / cream
    [0xAF3D55, 0xF4F6F8]    // rose / white
  ];
  const capBodyGeo = new THREE.CapsuleGeometry(0.075, 0.150, 8, 20);
  const capCapGeo  = new THREE.CapsuleGeometry(0.0795, 0.075, 8, 20);
  const tabGeo     = new THREE.SphereGeometry(0.105, 28, 18);

  function pillMaterial(hex) {
    return new THREE.MeshPhysicalMaterial({
      color: hex, roughness: 0.15, metalness: 0,
      clearcoat: 1, clearcoatRoughness: 0.03,
      envMapIntensity: 1.1, transparent: true
    });
  }
  function makeCapsule(spec) {
    const g = new THREE.Group();
    const a = new THREE.Mesh(capBodyGeo, pillMaterial(spec[0]));
    a.position.y = 0.052;
    const b = new THREE.Mesh(capCapGeo, pillMaterial(spec[1]));
    b.position.y = -0.083;                     // sleeve half, slightly fatter
    g.add(a, b);
    g.userData.mats = [a.material, b.material];
    return g;
  }
  function makeTablet() {
    const m = new THREE.Mesh(tabGeo, pillMaterial(0xF2F4F6));
    m.scale.set(1, 0.42, 1);                   // pressed tablet
    const g = new THREE.Group(); g.add(m);
    g.userData.mats = [m.material];
    return g;
  }
  function setPillOpacity(g, o) {
    g.userData.mats.forEach(m => { m.opacity = o; });
    g.visible = o > 0.02;
  }

  /* three pieces per wedge: two capsules and a tablet */
  const chamberPills = [];
  for (let i = 0; i < 7; i++) {
    const set = [], mid = i * SEG + SEG / 2;
    const spec = CAP_SPECS[i % CAP_SPECS.length];
    const defs = [
      { mk: () => makeCapsule(spec),                                  rr: 0.40, da: -0.13, lay: 1.10 },
      { mk: () => makeCapsule(CAP_SPECS[(i + 2) % CAP_SPECS.length]), rr: 0.62, da:  0.12, lay: 2.30 },
      { mk: () => makeTablet(),                                       rr: 0.52, da:  0.00, lay: 0.00 }
    ];
    defs.forEach(d => {
      const g = d.mk();
      g.userData.rest = new THREE.Vector3(
        Math.cos(mid + d.da) * d.rr, -0.045, Math.sin(mid + d.da) * d.rr);
      g.userData.spin = mid + d.da + d.lay;
      carousel.add(g); set.push(g);
    });
    chamberPills.push(set);
  }

  /* ---------- hub / button ---------- */
  const hub = new THREE.Group();
  const hubStem = new THREE.Mesh(new THREE.CylinderGeometry(0.144, 0.150, 0.50, 48), body);
  hubStem.position.y = 0.070; hub.add(hubStem);
  const hubCap = new THREE.Mesh(new THREE.CylinderGeometry(0.156, 0.156, 0.052, 48), body);
  hubCap.position.y = 0.336; hub.add(hubCap);
  const hubDome = new THREE.Mesh(new THREE.SphereGeometry(
    0.156, 48, 24, 0, Math.PI * 2, 0, Math.PI / 2), body);
  hubDome.scale.y = 0.30; hubDome.position.y = 0.362; hub.add(hubDome);
  product.add(hub);

  /* ---------- lid ---------- */
  const lidProfile = [
    V(0.152, 0.302), V(0.162, 0.332), V(0.190, 0.338), V(0.212, 0.324),
    V(0.300, 0.313), V(0.550, 0.303), V(0.750, 0.291), V(0.840, 0.277),
    V(0.864, 0.258),
    V(0.851, 0.244), V(0.750, 0.263), V(0.550, 0.275),
    V(0.300, 0.285), V(0.214, 0.296), V(0.190, 0.308),
    V(0.162, 0.304), V(0.152, 0.302)
  ];
  const lid = new THREE.Mesh(new THREE.LatheGeometry(lidProfile, 160), tint);
  lid.geometry.computeVertexNormals();
  product.add(lid);

  /* ---------- the latch: a shallow tray filling the window, hinged at
     the sill. Open, it hangs down like a little scoop — as in the photos. */
  const LSPAN = SPAN * 0.92, LA0 = A0 + (SPAN - LSPAN) / 2;
  const latchFaceProfile = [
    V(0.940, SILL_Y + 0.002), V(0.972, -0.205), V(0.996, -0.120),
    V(1.008, -0.030), V(1.006,  0.030), V(0.994,  0.070),
    V(0.960,  0.070), V(0.972,  0.028), V(0.974, -0.030),
    V(0.964, -0.120), V(0.944, -0.205), V(0.916, SILL_Y + 0.002),
    V(0.940, SILL_Y + 0.002)
  ];
  const latchG = new THREE.Group();
  const latchMesh = new THREE.Mesh(
    new THREE.LatheGeometry(latchFaceProfile, 24,
                            Math.PI / 2 - LA0 - LSPAN, LSPAN), tint);
  latchMesh.geometry.computeVertexNormals();
  /* the moulded step across the face — the grip that reads in every photo */
  const grip = new THREE.Mesh(new THREE.BoxGeometry(0.30, 0.052, 0.030), tint);
  grip.position.copy(DD).multiplyScalar(1.014); grip.position.y = -0.185;
  grip.rotation.y = -AMID + Math.PI / 2;
  /* tray side cheeks so the open flap reads as a scoop, not a sheet */
  const cheek = new THREE.BoxGeometry(0.085, 0.24, 0.014);
  const cheeks = [LA0 + 0.05, LA0 + LSPAN - 0.05].map(a => {
    const m = new THREE.Mesh(cheek, tint);
    m.position.set(Math.cos(a) * 0.945, -0.10, Math.sin(a) * 0.945);
    m.rotation.y = -a;
    return m;
  });
  /* hinge pivot: the sill */
  const P = DD.clone().multiplyScalar(0.930); P.y = SILL_Y;
  latchG.position.copy(P);
  [latchMesh, grip, ...cheeks].forEach(m => { m.position.sub(P); latchG.add(m); });
  product.add(latchG);
  const latchHome = latchG.position.clone();

  /* pieces that spill out through the open latch */
  const ejected = [];
  for (let i = 0; i < 7; i++) {
    const set = [];
    const spec = CAP_SPECS[i % CAP_SPECS.length];
    [makeCapsule(spec), makeCapsule(CAP_SPECS[(i + 2) % CAP_SPECS.length]),
     makeTablet()].forEach(g => {
      setPillOpacity(g, 0); product.add(g); set.push(g);
    });
    ejected.push(set);
  }

  scene.add(product);

  /* ---------- contact shadow ---------- */
  function blobTexture() {
    const N = 256, c = document.createElement("canvas");
    c.width = c.height = N;
    const x = c.getContext("2d");
    const g = x.createRadialGradient(N/2, N/2, N*0.04, N/2, N/2, N*0.48);
    g.addColorStop(0, "rgba(13,17,32,0.42)");
    g.addColorStop(0.5, "rgba(13,17,32,0.16)");
    g.addColorStop(1, "rgba(13,17,32,0)");
    x.fillStyle = g; x.fillRect(0, 0, N, N);
    return new THREE.CanvasTexture(c);
  }
  const shadowMat = new THREE.MeshBasicMaterial({
    map: blobTexture(), transparent: true, depthWrite: false });
  const shadow = new THREE.Mesh(new THREE.PlaneGeometry(3.6, 3.6), shadowMat);
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = -1.42;
  scene.add(shadow);

  /* ---------- state + frame loop ---------- */
  const S = {
    orbit: 0, fill: 0, lid: 0, turn: 0, press: 0, drawer: 0, eject: 0,
    explode: 0, ox: 0, oy: 0, camY: 0.35, camZ: 6.0, tilt: 0.42, scale: 1
  };
  const cur = { ...S };
  const FIELDS = Object.keys(S);
  let mx = 0, my = 0, px = 0, py = 0;
  addEventListener("pointermove", e => {
    mx = (e.clientX / innerWidth - 0.5) * 2;
    my = (e.clientY / innerHeight - 0.5) * 2;
  }, { passive: true });

  const clamp = (v, a = 0, b = 1) => v < a ? a : v > b ? b : v;
  const lerp  = (a, b, t) => a + (b - a) * t;
  const easeIO = t => t < .5 ? 4*t*t*t : 1 - Math.pow(-2*t + 2, 3) / 2;
  const easeOut = t => 1 - Math.pow(1 - t, 3);

  let day = -1;
  const onDay = opts.onDay || (() => {});
  const clock = new THREE.Clock();
  const tmp = new THREE.Vector3(), off = new THREE.Vector3();
  const invQ = new THREE.Quaternion(), hingeQ = new THREE.Quaternion();

  function frame() {
    const t = clock.getElapsedTime();
    const k = REDUCED ? 1 : 0.10;
    for (const f of FIELDS) cur[f] = lerp(cur[f], S[f], k);
    px = lerp(px, mx, 0.05); py = lerp(py, my, 0.05);

    const bob = REDUCED ? 0 : Math.sin(t * 0.62) * 0.026;
    const sway = REDUCED ? 0 : Math.sin(t * 0.44) * 0.020;

    product.position.set(cur.ox, cur.oy + bob, 0);
    product.rotation.set(cur.tilt + sway * 0.3 + py * 0.09,
                         cur.orbit + px * 0.14, sway * 0.45);
    product.scale.setScalar(cur.scale);

    /* lid: screen-space lift, stacks for the exploded view */
    const L  = easeIO(clamp(cur.lid));
    const Ex = easeIO(clamp(cur.explode));
    invQ.copy(product.quaternion).invert();
    off.set(L * 0.30 * (1 - Ex), L * (1.32 - Ex * 0.30), 0).applyQuaternion(invQ);
    lid.position.copy(off);
    lid.rotation.set(L * (0.30 - Ex * 0.30), L * 0.55, L * (-0.26 + Ex * 0.26));

    /* carousel detents */
    const raw = clamp(cur.turn) * 7;
    const idx = Math.min(6, Math.floor(raw));
    const loc = clamp((raw - idx) * 1.6);
    carousel.rotation.y = -(idx + easeIO(loc)) * SEG;
    if (idx !== day) { day = idx; onDay(day); }

    /* button */
    const stepPress = Math.sin(clamp(loc) * Math.PI);
    const press = Math.max(stepPress * clamp(cur.turn > 0 ? 1 : 0), clamp(cur.press));

    /* fill: pieces drop into the wedges, staggered */
    const F = clamp(cur.fill);
    chamberPills.forEach((set, i) => {
      const local = clamp((F * 7 - i) / 1.0);
      const gone  = clamp(cur.eject * 7 - i);
      set.forEach((g, kk) => {
        const rest = g.userData.rest;
        const st = clamp((local * 1.25) - kk * 0.12);
        const drop = 1 - easeOut(st);
        g.position.set(rest.x, rest.y + drop * 1.55, rest.z);
        const settle = st > 0.92 ? Math.sin((st - 0.92) / 0.08 * Math.PI) * 0.05 : 0;
        g.rotation.set(Math.PI / 2 + drop * 3.2, 0,
                       g.userData.spin + drop * 2.4 + settle);
        setPillOpacity(g, clamp(st * 4) * (1 - clamp(gone * 3)));
      });
    });

    /* the latch swings DOWN on its bottom hinge */
    const D = easeIO(clamp(cur.drawer));
    hingeQ.setFromAxisAngle(TT, -(D * 1.10 + Ex * 0.30));
    latchG.quaternion.copy(hingeQ);
    latchG.position.copy(latchHome).addScaledVector(DD, Ex * 0.45);

    /* medication spills through the opening: slides over the sill, then falls */
    const E = clamp(cur.eject);
    ejected.forEach((set, i) => {
      const f = clamp((E * 7.6 - i) / 2.5);
      set.forEach((g, kk) => {
        const st = clamp((f * 1.22) - kk * 0.14);
        if (st <= 0.001 || st >= 1) { setPillOpacity(g, 0); return; }
        const slide = clamp(st / 0.35);            // radial slide to the sill
        const fall  = clamp((st - 0.35) / 0.65);   // then gravity takes it
        const out = 0.66 + slide * 0.34 + fall * 0.24 + kk * 0.05;
        tmp.copy(DD).multiplyScalar(out);
        tmp.z += (kk - 1) * 0.09; tmp.y = -0.19 - slide * 0.04;
        off.set(0, -(fall * fall * 1.9), 0).applyQuaternion(invQ);
        g.position.copy(tmp).add(off);
        g.rotation.set(Math.PI / 2 + fall * 2.8 + kk, st * 1.6, fall * 3.4 + kk * 1.3);
        setPillOpacity(g, clamp(st * 6) * (1 - clamp((st - 0.72) / 0.28)));
      });
    });

    /* exploded view along screen-up */
    off.set(0, Ex * 0.30, 0).applyQuaternion(invQ);
    carousel.position.copy(off);
    hub.position.set(off.x, off.y - press * 0.075, off.z);
    off.set(0, -Ex * 0.34, 0).applyQuaternion(invQ);
    shell.position.copy(off);
    wellFloor.position.set(off.x, off.y - 0.152, off.z);

    /* contact shadow follows the float */
    const height = clamp((product.position.y + 1.42) / 1.9);
    shadow.position.x = product.position.x;
    shadow.scale.setScalar(0.85 + (1 - height) * 0.55);
    shadowMat.opacity = 0.9 - height * 0.55;

    camera.position.set(px * 0.13, cur.camY * fit, cur.camZ * fit);
    camera.lookAt(cur.ox * 0.5, 0, 0);
    renderer.render(scene, camera);
  }

  function resize() {
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h; camera.updateProjectionMatrix();

    /* A perspective camera holds the VERTICAL extent constant, so the horizontal
       extent shrinks as the viewport narrows. The product is a wide flat disc,
       so at a phone's ~0.46 aspect it runs far outside the frame and collides
       with the copy. Pull the camera back to hold roughly the horizontal
       framing the scene was composed at. Capped so it never dollies to a dot. */
    fit = Math.min(2.4, Math.max(1, REF_ASPECT / camera.aspect));
  }

  return { set(n) { Object.assign(S, n); }, frame, resize,
           get day() { return day; } };
}
